import React from "react";
import Form from "../common/form";
import { Registration } from "./Registrations/registration";
import "./proposalForm.css";
import {
  getPrimaryRegistrationValue,
  getRiskToleranceOptionsForProgram,
  shouldShowBondTypeOptions,
} from "../../utils/proposalUtils";

export class ProposalFormDetails2 extends Form {
  _isMounted = true;
  alertUp = false;
  isUpdatingFromPercent = false; // Flag to prevent circular updates
  isUpdatingFromAmount = false; // Flag to prevent circular updates
  state = {
    bondOptions: false,
    program: this.props.values.program ? this.props.values.program : "",
    riskTolerance: [],
    isNewFusionClient: true,
    fusionAccountNumbers: [""],
    fusionPercentage: this.props.values.fusionPercentage || "",
  };

  areArraysEqual = (left = [], right = []) => {
    if (left === right) return true;
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    if (left.length !== right.length) return false;
    return left.every((value, index) => value === right[index]);
  };

  syncProgramSelectionState = () => {
    const proposalTypes = this.props.lists?.proposalTypes || [];
    const registrationTypes = this.props.lists?.registrationTypes || [];
    const selectedProgram =
      proposalTypes.find(
        (proposalType) => proposalType.name === this.props.values.program
      ) || "";
    const primaryRegistrationValue = getPrimaryRegistrationValue(
      this.props.values.registrations
    );
    const nextBondOptions = shouldShowBondTypeOptions(
      selectedProgram,
      primaryRegistrationValue,
      registrationTypes
    );
    const nextOptionsTM =
      typeof selectedProgram === "object" &&
      (Array.isArray(selectedProgram.taxableBonds) ||
        Array.isArray(selectedProgram.municipalBonds))
        ? this.props.values.optionsTM || "Taxable"
        : "";
    const nextRiskTolerance = getRiskToleranceOptionsForProgram(
      selectedProgram,
      nextOptionsTM,
      primaryRegistrationValue,
      registrationTypes
    );

    if (nextOptionsTM !== this.props.values.optionsTM) {
      this.props.handles.handleDefault("optionsTM", nextOptionsTM);
    }

    if (
      this.props.values.riskTolerance &&
      !nextRiskTolerance.includes(this.props.values.riskTolerance)
    ) {
      this.props.handles.handleDefault("riskTolerance", "");
    }

    const shouldUpdateState =
      this.state.program !== selectedProgram ||
      this.state.bondOptions !== nextBondOptions ||
      !this.areArraysEqual(this.state.riskTolerance, nextRiskTolerance);

    if (shouldUpdateState && this._isMounted) {
      this.setState({
        program: selectedProgram,
        bondOptions: nextBondOptions,
        riskTolerance: nextRiskTolerance,
      });
    }
  };

  componentDidMount = () => {
    this.syncProgramSelectionState();

    // Initialize fusion percentage from props if available
    if (this.props.fusion && this.props.values.fusionPercentage) {
      this.setState({ fusionPercentage: this.props.values.fusionPercentage });
    }

    // Initialize fusion account data in main form state
    // This runs for both fusion forms AND when copying proposals with account numbers
    if (this._isMounted) {
      const programName =
        this.state.program?.name ||
        this.state.program ||
        this.props.values.program ||
        "";
      const isFusionProgram = programName.toLowerCase().includes("fusion");

      // Initialize if in fusion mode OR if we have fusion-related data from a copy
      if (
        this.props.fusion ||
        isFusionProgram ||
        this.props.values.fusionAccountNumbers?.length > 0 ||
        this.props.values.isNewFusionClient !== undefined
      ) {
        // Set initial values from props or defaults
        const accountNumbers = this.props.values.fusionAccountNumbers || [];

        // Determine client type based on account numbers presence
        // If we have account numbers, it's an existing client (isNewFusionClient = false)
        const isNewClient =
          this.props.values.isNewFusionClient !== undefined
            ? this.props.values.isNewFusionClient
            : accountNumbers.length === 0; // Default to new client only if no account numbers

        this.setState({
          isNewFusionClient: isNewClient,
          fusionAccountNumbers:
            accountNumbers.length > 0 ? [...accountNumbers, ""] : [""],
        });

        // Update main form state
        this.props.handles.handleDefault("isNewFusionClient", isNewClient);
        this.props.handles.handleDefault(
          "fusionAccountNumbers",
          accountNumbers
        );
      }
    }
  };

  componentWillUnmount = () => {
    this._isMounted = false;
  };

  componentDidUpdate(prevProps) {
    // Update fusion percentage if props change
    if (
      this.props.fusion &&
      prevProps.values.fusionPercentage !==
        this.props.values.fusionPercentage &&
      this.props.values.fusionPercentage !== this.state.fusionPercentage
    ) {
      this.setState({
        fusionPercentage: this.props.values.fusionPercentage || "",
      });
    }

    if (
      prevProps.values.program !== this.props.values.program ||
      prevProps.values.optionsTM !== this.props.values.optionsTM ||
      prevProps.values.riskTolerance !== this.props.values.riskTolerance ||
      prevProps.values.registrations !== this.props.values.registrations ||
      prevProps.lists?.proposalTypes !== this.props.lists?.proposalTypes ||
      prevProps.lists?.registrationTypes !== this.props.lists?.registrationTypes
    ) {
      this.syncProgramSelectionState();
    }
  }

  continue = (e) => {
    e.preventDefault();

    // If this is a fusion form, call the addToFusion function instead of nextStep
    if (this.props.fusion && this.props.addToFusion) {
      // Prepare the complete form data for fusion
      const formData = {
        ...this.props.values,
        registrations: JSON.parse(
          JSON.stringify(this.props.values.registrations || [])
        ), // Deep copy to prevent shared references
        proposalType:
          this.props.values.proposalType || this.props.values.program,
        fusionAmount: Math.round(
          parseFloat(this.props.values.assetValue?.replace(/[^0-9.-]+/g, "")) ||
            0
        ),
        fusionPercentage:
          Math.round((parseFloat(this.state.fusionPercentage) || 0) * 100) /
          100,
        isNewFusionClient: this.state.isNewFusionClient,
        fusionAccountNumbers: this.state.fusionAccountNumbers.filter(
          (num) => num.trim() !== ""
        ),
      };

      // Call the parent's addToFusion function
      this.props.addToFusion(formData);
    } else {
      // Normal flow - go to next step
      this.props.nextStep();
    }
  };

  back = (e) => {
    e.preventDefault();
    this.props.prevStep();
  };

  localStringToNumber(s) {
    return Number(String(s).replace(/[^0-9.-]+/g, ""));
  }

  // Validation function to check if income needs exceeds account value
  validateIncomeVsAccount = () => {
    const { values } = this.props;
    const incomeNeeds = this.localStringToNumber(values.incomeNeeds || 0);
    const accountValue = this.localStringToNumber(values.assetValue || 0);

    if (accountValue === 0) {
      return { valid: false, reason: "account_empty" }; // Account value not set
    }

    if (incomeNeeds > accountValue) {
      return { valid: false, reason: "exceeds_account" }; // Income exceeds account
    }

    return { valid: true, reason: "valid" };
  };

  hasIncomeNeedsValue = () => {
    const { values } = this.props;
    const incomeNeeds = this.localStringToNumber(values.incomeNeeds || 0);
    return !!values.incomeNeeds && incomeNeeds > 0;
  };

  // Get border color for Account Value field when Active Income Strategy
  getAccountValueBorderColor = () => {
    const { values } = this.props;
    const validation = this.validateIncomeVsAccount();

    // If no account value, neutral border
    if (!values.assetValue || values.assetValue === "$0") {
      return "#ced4da";
    }

    // If income exceeds account value, red border for account too
    if (validation.reason === "exceeds_account") {
      return "red";
    }

    // Valid - green border
    return "green";
  };

  // Get border color for Account Value field considering program limits
  getAssetValueBorderColor = () => {
    const { values } = this.props;
    const { program } = this.state;

    // If no account value, neutral border
    if (!values.assetValue || values.assetValue === "$0") {
      return "#ced4da";
    }

    const currentAssetValue =
      parseFloat(values.assetValue.replace(/[^0-9.-]+/g, "")) || 0;

    // Check program limits if they exist - use fusionLimits when fusion flag is set
    const limitsKey = this.props.fusion ? "fusionLimits" : "limits";
    if (program && program[limitsKey] && currentAssetValue > 0) {
      if (
        currentAssetValue > Number(program[limitsKey].max) ||
        currentAssetValue < Number(program[limitsKey].min)
      ) {
        return "red";
      }
    }

    // Valid - green border
    return "green";
  };

  handleFusionClientTypeChange = (e) => {
    const isNew = e.target.value === "new";
    this.setState({
      isNewFusionClient: isNew,
      fusionAccountNumbers: isNew ? [""] : [""],
    });
    // Update main form state
    this.props.handles.handleDefault("isNewFusionClient", isNew);
    this.props.handles.handleDefault("fusionAccountNumbers", isNew ? [] : []);
  };

  handleAccountNumberChange = (index, value) => {
    const newAccountNumbers = [...this.state.fusionAccountNumbers];
    newAccountNumbers[index] = value;

    // Add a new empty field if this is the last field and it's not empty
    if (index === newAccountNumbers.length - 1 && value.trim() !== "") {
      newAccountNumbers.push("");
    }

    // Remove empty fields except the last one if there are more than one
    const filteredNumbers = newAccountNumbers.filter(
      (num, idx) =>
        num.trim() !== "" ||
        idx === newAccountNumbers.length - 1 ||
        newAccountNumbers.length === 1
    );

    this.setState({ fusionAccountNumbers: filteredNumbers });
    // Update main form state with filtered account numbers (excluding empty ones)
    this.props.handles.handleDefault(
      "fusionAccountNumbers",
      filteredNumbers.filter((num) => num.trim() !== "")
    );
  };

  handleFusionPercentageChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");

    // Prevent negative values
    if (parseFloat(value) < 0) {
      return;
    }

    // Keep as string during typing to preserve decimal places being entered
    this.setState({ fusionPercentage: value });
  };

  handleFusionPercentageBlur = (e) => {
    // Prevent circular updates
    if (this.isUpdatingFromAmount) {
      return;
    }

    this.isUpdatingFromPercent = true;

    const value = e.target.value.replace(/[^0-9.]/g, "");
    const percentage = value ? Math.max(0, parseFloat(value)) : 0; // Ensure non-negative

    // Calculate remaining percentage available
    const totalFusionAmount = this.getTotalFusionAmount();
    const currentFusionTotal = this.getCurrentFusionTotal();
    const remainingAmount = totalFusionAmount - currentFusionTotal;
    let remainingPercentage =
      totalFusionAmount > 0
        ? Math.round((remainingAmount / totalFusionAmount) * 100 * 100) / 100
        : 100;

    // Allow any percentage without validation alert - final submit logic will handle validation
    // Note: Edit mode percentage calculation available if needed for future validation

    this.props.handles.handleDefault("fusionPercentage", percentage);

    // Auto-calculate amount based on percentage if total fusion amount is available
    if (totalFusionAmount > 0 && percentage >= 0) {
      const amount = Math.round((percentage / 100) * totalFusionAmount);
      const formattedAmount = amount.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        currency: "USD",
        style: "currency",
        currencyDisplay: "symbol",
      });

      // Update the form value directly and trigger re-render
      this.props.handles.handleDefault("assetValue", formattedAmount);
      this.props.handles.handleDefault("fusionAmount", amount);
    }

    // Reset the flag after a short delay to allow DOM updates
    setTimeout(() => {
      this.isUpdatingFromPercent = false;
    }, 10);
  };

  // Get total fusion amount from props (should be passed from parent)
  getTotalFusionAmount = () => {
    return this.props.totalFusionAmount || 0;
  };

  // Get current total of all existing proposals in fusion
  getCurrentFusionTotal = () => {
    return this.props.currentFusionTotal || 0;
  };

  // Calculate remaining fusion amount
  getRemainingFusionAmount = () => {
    return this.getTotalFusionAmount() - this.getCurrentFusionTotal();
  };

  // Calculate remaining fusion percentage
  getRemainingFusionPercentage = () => {
    return (
      Math.round(
        (this.getRemainingFusionAmount() / this.getTotalFusionAmount()) *
          100 *
          100
      ) / 100
    );
  };

  render() {
    var options = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      currency: "USD",
      style: "currency",
      currencyDisplay: "symbol",
    };

    // Calculate remaining fusion percentage once per render (only when in fusion mode)
    const remainingFusionPercentage = this.props.fusion
      ? this.getRemainingFusionPercentage()
      : 0;

    const { values, handles } = this.props;
    const { registrationTypes, registrationCategoryTypes } = this.props.lists;
    const {
      riskTolerance,
      program,
      bondOptions,
      isNewFusionClient,
      fusionAccountNumbers,
    } = this.state;
    let disableNext2 = true;

    const isFusionProgram = String(program.name || program)
      .toLowerCase()
      .includes("fusion");

    // Validate fusion account numbers for existing clients
    const hasValidFusionAccountNumbers =
      isNewFusionClient ||
      fusionAccountNumbers.some((num) => num.trim() !== "");

    // Validate fusion percentage when in fusion mode
    const isFusionPercentageValid =
      !this.props.fusion ||
      (this.state.fusionPercentage !== "" &&
        parseFloat(this.state.fusionPercentage) > 0);

    // Active Income no longer requires a distribution plan up front. If the
    // optional income need is entered, keep the existing amount validation.
    const incomeValidation = this.validateIncomeVsAccount();
    const isIncomeValid =
      values["program"] !== "Active Income Strategy" ||
      !this.hasIncomeNeedsValue() ||
      incomeValidation.valid;

    // Validate that risk tolerance value exists in current program's options (skip only for fusion programs)
    const isRiskToleranceValid =
      String(program.name || program)
        .toLowerCase()
        .includes("fusion") ||
      (values.riskTolerance &&
        values.riskTolerance.trim() !== "" &&
        riskTolerance &&
        riskTolerance.includes(values.riskTolerance));

    if (
      /\d/.test(values.assetValue) &&
      values.assetValue !== "$0" &&
      values.assetValue !== "0" &&
      values.assetValue !== 0 &&
      isRiskToleranceValid &&
      isIncomeValid &&
      isFusionPercentageValid &&
      (isFusionProgram
        ? hasValidFusionAccountNumbers
        : values.registrations[0].value !== "")
    ) {
      disableNext2 = false;
    }

    return (
      <div>
        <h1>
          {this.props.fusion ? "Strategy Details:" : "Proposal Details:"}
        </h1>
        <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
          All fields are required unless marked <em>(optional)</em>.
          Fields turn <span style={{ color: "green", fontWeight: "bold" }}>green</span> when completed.
        </p>
        {this.props.fusion && (
          <React.Fragment>
            {this.getTotalFusionAmount() > 0 && (
              <div className="alert alert-info">
                <div>
                  <strong>Fusion Portfolio Summary:</strong>
                </div>
                <div>
                  Total Fusion Amount: $
                  {this.getTotalFusionAmount().toLocaleString()}
                </div>
                <div>
                  Current Total Allocated: $
                  {this.getCurrentFusionTotal().toLocaleString()}
                </div>
                <div>
                  Remaining to Allocate: $
                  {this.getRemainingFusionAmount().toLocaleString()}
                </div>
                <div>
                  Remaining Percentage Available: {remainingFusionPercentage}%
                </div>
                <div className="form-group">
                  <label>
                    <strong>Percentage of Total Fusion</strong>
                  </label>
                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary mr-2"
                      onClick={() => {
                        this.setState({
                          fusionPercentage: remainingFusionPercentage,
                        });
                        this.props.handles.handleDefault(
                          "fusionPercentage",
                          remainingFusionPercentage
                        );

                        // Auto-calculate amount based on percentage
                        const totalFusionAmount = this.getTotalFusionAmount();
                        if (
                          totalFusionAmount > 0 &&
                          remainingFusionPercentage >= 0
                        ) {
                          const amount = Math.round(
                            (remainingFusionPercentage / 100) *
                              totalFusionAmount
                          );
                          const formattedAmount = amount.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                              currency: "USD",
                              style: "currency",
                              currencyDisplay: "symbol",
                            }
                          );

                          this.props.handles.handleDefault(
                            "assetValue",
                            formattedAmount
                          );
                          this.props.handles.handleDefault(
                            "fusionAmount",
                            amount
                          );
                        }
                      }}
                      title="Auto-fill remaining percentage"
                    >
                      {remainingFusionPercentage}
                    </button>
                    <input
                      type="text"
                      className="form-control flex-grow-1"
                      style={{
                        borderLeftColor:
                          this.state.fusionPercentage !== ""
                            ? "green"
                            : "#ced4da",
                        borderLeftWidth: "5px",
                      }}
                      value={this.state.fusionPercentage || ""}
                      onChange={this.handleFusionPercentageChange}
                      onBlur={this.handleFusionPercentageBlur}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        )}
        {values["program"] === "Active Income Strategy" && (
          <div className="form-group">
            <label>
              <strong>Income Needs Per Month <em>(optional)</em></strong>
            </label>
            <input
              placeholder="numbers only, no cents"
              onFocus={(e) => {
                var value = e.target.value;
                e.target.value = value ? this.localStringToNumber(value) : "";
              }}
              onBlur={(e) => {
                var value = e.target.value;

                var options = {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                  currency: "USD",
                  style: "currency",
                  currencyDisplay: "symbol",
                };

                e.target.value = value
                  ? this.localStringToNumber(value).toLocaleString(
                      undefined,
                      options
                    )
                  : "";
              }}
              name="incomeNeeds"
              id="incomeNeeds"
              className="form-control"
              onChange={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
                // Prevent negative values
                if (parseFloat(e.target.value) < 0) {
                  e.target.value = "";
                }
                handles.handleChange("incomeNeeds")(e);
              }}
              defaultValue={
                values.incomeNeeds
                  ? Number(
                      String(values.incomeNeeds).replace(/[^0-9.-]+/g, "")
                    ).toLocaleString(undefined, options)
                  : ""
              }
            />
          </div>
        )}
        <div className="form-group">
          <label>
            <strong>Account Value</strong>
          </label>
          <input
            placeholder="numbers only, no cents "
            onFocus={(e) => {
              let value = e.target.value;

              e.target.value = value ? this.localStringToNumber(value) : "";
            }}
            onBlur={(e) => {
              let value = e.target.value;
              let handleDefaultComplete = false;
              e.target.value = value
                ? this.localStringToNumber(value).toLocaleString(
                    undefined,
                    options
                  )
                : "";
              this.props.lists.proposalTypes.forEach((element) => {
                const limitsKey = this.props.fusion ? "fusionLimits" : "limits";
                if (values.program === element.name && element[limitsKey]) {
                  if (
                    e.target.value.replace(/[^0-9]/g, "") >
                    Number(element[limitsKey].max)
                  ) {
                    handleDefaultComplete = true;
                    handles.handleDefault("assetValue", 0);
                    if (!this.alertUp) {
                      alert(
                        `${
                          values.program
                        } has a maximum of ${this.localStringToNumber(
                          element[limitsKey].max
                        ).toLocaleString(undefined, options)}${
                          this.props.fusion ? " in fusion" : ""
                        }`
                      );
                      this.alertUp = true;
                    }
                  }
                  if (
                    e.target.value.replace(/[^0-9]/g, "") <
                    Number(element[limitsKey].min)
                  ) {
                    handleDefaultComplete = true;
                    handles.handleDefault("assetValue", 0);
                    if (!this.alertUp) {
                      alert(
                        `${
                          values.program
                        } has a minimum of ${this.localStringToNumber(
                          element[limitsKey].min
                        ).toLocaleString(undefined, options)}${
                          this.props.fusion ? " in fusion" : ""
                        }`
                      );
                      this.alertUp = true;
                    }
                  }
                }
              });
              if (!handleDefaultComplete) {
                handles.handleDefault("assetValue", e.target.value);
                this.alertUp = false;

                // Auto-calculate percentage for fusion if total fusion amount is available
                if (
                  this.props.fusion &&
                  this.getTotalFusionAmount() > 0 &&
                  !this.isUpdatingFromPercent
                ) {
                  this.isUpdatingFromAmount = true;

                  const amount = this.localStringToNumber(e.target.value);
                  const totalFusionAmount = this.getTotalFusionAmount();
                  const percentage =
                    totalFusionAmount > 0
                      ? Math.round((amount / totalFusionAmount) * 100 * 100) /
                        100
                      : 0;

                  this.setState({ fusionPercentage: percentage });
                  this.props.handles.handleDefault(
                    "fusionPercentage",
                    percentage
                  );
                  this.props.handles.handleDefault("fusionAmount", amount);

                  // Reset the flag after a short delay
                  setTimeout(() => {
                    this.isUpdatingFromAmount = false;
                  }, 10);
                }
              }
            }}
            name="assetValue"
            id="assetValue"
            className="form-control"
            style={{
              borderLeftColor:
                values["program"] === "Active Income Strategy"
                  ? this.hasIncomeNeedsValue()
                    ? this.getAccountValueBorderColor()
                    : this.getAssetValueBorderColor()
                  : this.getAssetValueBorderColor(),
              borderLeftWidth: "5px",
            }}
            onChange={(e) => {
              e.target.value = e.target.value.replace(/\D/g, "");
              // Prevent negative values
              if (parseFloat(e.target.value) < 0) {
                e.target.value = "";
              }
              handles.handleChange("assetValue")(e);
            }}
            value={
              values.assetValue
                ? Number(
                    String(values.assetValue).replace(/[^0-9.-]+/g, "")
                  ).toLocaleString(undefined, options)
                : ""
            }
          />
        </div>

        {/* Fusion percentage field and summary */}

        {!String(this.state.program.name || this.state.program)
          .toLowerCase()
          .includes("fusion") && (
          <React.Fragment>
            {Object.entries(
              typeof this.props.registrations !== "string"
                ? this.props.registrations
                : JSON.parse(
                    String(this.props.registrations).replace(/'/g, '"')
                  )
            ).map((r, index) => (
              <Registration
                key={index}
                object={r}
                index={index}
                defaultValue={""}
                registrationTypes={registrationTypes}
                registrationCategoryTypes={registrationCategoryTypes}
                handleRegistrations={handles.handleRegistrations}
                percentageShow={
                  String(this.state.program).toLowerCase().includes("uma")
                    ? true
                    : false
                }
              />
            ))}
            {String(this.state.program).toLowerCase().includes("uma") && (
              <button
                className="btn btn-primary"
                style={{ marginBottom: "15px" }}
                onClick={() => {
                  handles.handleAddRegistration(
                    "registration" +
                      Object.keys(this.props.values.registrations).length
                  );
                }}
              >
                Add Registration
              </button>
            )}
          </React.Fragment>
        )}

        {!String(this.state.program.name || this.state.program)
          .toLowerCase()
          .includes("fusion") && (
          <div className="form-group">
            <label>
              <strong>Risk Tolerance</strong>
            </label>
            <div></div>
            {bondOptions && (
              <React.Fragment>
                <div className="form-check form-check-inline">
                  <input
                    style={{ height: "15px", width: "15px" }}
                    defaultChecked={
                      values["optionsTM"] === "Taxable" ||
                      values["optionsTM"] === ""
                    }
                    onChange={() => {
                      handles.handleDefault("optionsTM", "Taxable");
                      handles.handleDefault("riskTolerance", "");
                      this.setState({
                        riskTolerance: program["taxableBonds"],
                      });
                    }}
                    className="form-check-input"
                    type="radio"
                    name="inlineRadioOptions"
                    id="inlineRadio1"
                    value="Taxable"
                  />
                  <label
                    className="form-check-label"
                    htmlFor="inlineRadio1"
                  >
                    Taxable Bonds
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    style={{ height: "15px", width: "15px" }}
                    defaultChecked={values["optionsTM"] === "Municipal"}
                    onChange={() => {
                      handles.handleDefault("optionsTM", "Municipal");
                      handles.handleDefault("riskTolerance", "");
                      this.setState({
                        riskTolerance: program["municipalBonds"],
                      });
                    }}
                    className="form-check-input"
                    type="radio"
                    name="inlineRadioOptions"
                    id="inlineRadio2"
                    value="Municipal"
                  />
                  <label
                    className="form-check-label"
                    htmlFor="inlineRadio2"
                  >
                    Municipal Bonds
                  </label>
                </div>
              </React.Fragment>
            )}
            <select
              name="riskTolerance"
              id="riskTolerance"
              className="form-control"
              style={{
                borderLeftColor:
                  values.riskTolerance &&
                  values.riskTolerance.trim() !== "" &&
                  riskTolerance &&
                  riskTolerance.includes(values.riskTolerance)
                    ? "green"
                    : "#ced4da",
                borderLeftWidth: "5px",
              }}
              value={values.riskTolerance ? values.riskTolerance : ""}
              onChange={handles.handleChange("riskTolerance")}
            >
              {riskTolerance && (
                <React.Fragment>
                  <option value={""} key={riskTolerance.length + 1}>
                    Select Risk Tolerance
                  </option>
                  {riskTolerance.map((pt, index) => (
                    <option value={pt} key={index}>
                      {pt}
                    </option>
                  ))}
                </React.Fragment>
              )}
            </select>
          </div>
        )}

        {isFusionProgram && (
          <div className="form-group">
            <label>
              <strong>Fusion Client</strong>
            </label>
            <div>
              <div className="form-check form-check-inline">
                <input
                  style={{ height: "15px", width: "15px" }}
                  className="form-check-input"
                  type="radio"
                  name="fusionClientTypeOptions"
                  id="newFusionClientRadio"
                  value="new"
                  checked={isNewFusionClient}
                  onChange={this.handleFusionClientTypeChange}
                />
                <label
                  className="form-check-label"
                  htmlFor="newFusionClientRadio"
                >
                  New
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  style={{ height: "15px", width: "15px" }}
                  className="form-check-input"
                  type="radio"
                  name="fusionClientTypeOptions"
                  id="existingFusionClientRadio"
                  value="existing"
                  checked={!isNewFusionClient}
                  onChange={this.handleFusionClientTypeChange}
                />
                <label
                  className="form-check-label"
                  htmlFor="existingFusionClientRadio"
                >
                  Existing
                </label>
              </div>
            </div>

            {!isNewFusionClient && (
              <div style={{ marginTop: "15px" }}>
                <label>
                  <strong>Account Number(s)</strong>
                </label>
                {fusionAccountNumbers.map((accountNumber, index) => {
                  return (
                    <div key={index} style={{ marginBottom: "8px" }}>
                      <input
                        type="text"
                        className="form-control"
                        style={{
                          borderLeftColor:
                            accountNumber.trim() !== "" ||
                            (index > 0 &&
                              index === fusionAccountNumbers.length - 1)
                              ? "green"
                              : "#ced4da",
                          borderLeftWidth: "5px",
                        }}
                        value={accountNumber}
                        onChange={(e) =>
                          this.handleAccountNumberChange(index, e.target.value)
                        }
                        placeholder={`Enter last four here`}
                        pattern="[A-Za-z0-9]*"
                        maxLength="4"
                        title="Please enter letters and numbers only (no symbols)"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            // If this is not the last field or the field has content, move to next
                            if (index < fusionAccountNumbers.length - 1) {
                              // Focus next existing field
                              const nextInput =
                                e.target.parentElement.nextElementSibling?.querySelector(
                                  "input"
                                );
                              if (nextInput) {
                                nextInput.focus();
                              }
                            } else if (accountNumber.trim() !== "") {
                              // Add new field and focus it after state update
                              this.handleAccountNumberChange(
                                index,
                                accountNumber
                              );
                              setTimeout(() => {
                                const newInput =
                                  e.target.parentElement.nextElementSibling?.querySelector(
                                    "input"
                                  );
                                if (newInput) {
                                  newInput.focus();
                                }
                              }, 0);
                            }
                          } else if (!/[A-Za-z0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div>
          <button
            className="btn btn-primary"
            onClick={this.back}
            style={{
              marginBottom: "10px",
              paddingLeft: "30px",
              paddingRight: "30px",
            }}
          >
            Back
          </button>
          <button
            className="btn btn-primary"
            onClick={(e) => {
              let handleDefaultComplete = false;

              // Check optional income needs validation for Active Income Strategy
              if (
                values["program"] === "Active Income Strategy" &&
                this.hasIncomeNeedsValue()
              ) {
                const incomeValidation = this.validateIncomeVsAccount();
                if (
                  !incomeValidation.valid &&
                  incomeValidation.reason === "exceeds_account"
                ) {
                  alert(
                    "Income Needs Per Month cannot exceed the Account Value. Please adjust one of these values."
                  );
                  return;
                }
              }

              this.props.lists.proposalTypes.forEach((element) => {
                const limitsKey = this.props.fusion ? "fusionLimits" : "limits";
                if (values.program === element.name && element[limitsKey]) {
                  if (
                    values.assetValue.replace(/[^0-9]/g, "") >
                    Number(element[limitsKey].max)
                  ) {
                    handleDefaultComplete = true;
                    handles.handleDefault("assetValue", 0);
                    if (!this.alertUp) {
                      alert(
                        `${
                          values.program
                        } has a maximum of ${this.localStringToNumber(
                          element[limitsKey].max
                        ).toLocaleString(undefined, options)}${
                          this.props.fusion ? " in fusion" : ""
                        }`
                      );
                      this.alertUp = true;
                    }
                  }
                  if (
                    values.assetValue.replace(/[^0-9]/g, "") <
                    Number(element[limitsKey].min)
                  ) {
                    handleDefaultComplete = true;
                    handles.handleDefault("assetValue", 0);
                    if (!this.alertUp) {
                      alert(
                        `${
                          values.program
                        } has a minimum of ${this.localStringToNumber(
                          element[limitsKey].min
                        ).toLocaleString(undefined, options)}${
                          this.props.fusion ? " in fusion" : ""
                        }`
                      );
                      this.alertUp = true;
                    }
                  }
                }
              });
              if (!handleDefaultComplete) {
                this.continue(e);
              }
            }}
            style={{
              float: "right",
              marginBottom: "10px",
              paddingLeft: "30px",
              paddingRight: "30px",
            }}
            disabled={disableNext2}
          >
            {this.props.fusion
              ? this.props.formRequestType === "Edit"
                ? "Update Proposal"
                : "Add to Fusion"
              : "Next"}
          </button>
        </div>
      </div>
    );
  }
}

export default ProposalFormDetails2;
